// Follow-up hooks. The four lead-scoped mutations live alongside an
// org-scoped `useActiveFollowUps` poller that powers the reminder overlay.
// Cache-invalidation fans out to the lead list + the lead's activity feed +
// the active-reminders key so the engine clears the popup it just fired.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { useSelectedCompany } from "../contexts/SelectedCompanyProvider";

const LEADS_KEY = ["crm", "leads"];
const ACTIVE_KEY = ["crm", "followups", "active"];
const PENDING_KEY = ["crm", "followups", "pending"];

function invalidate(qc, leadId) {
  qc.invalidateQueries({ queryKey: LEADS_KEY });
  qc.invalidateQueries({ queryKey: ACTIVE_KEY });
  // Every reader of the due-sorted pending feed (Follow-ups screen, dashboard
  // rail) must drop the row the moment it is completed, snoozed or cleared.
  qc.invalidateQueries({ queryKey: PENDING_KEY });
  if (leadId) {
    qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
  }
  // Org-wide activity feed picks up the new FOLLOWUP_* entry.
  qc.invalidateQueries({ queryKey: ["crm", "activities", "feed"] });
  qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
}

export function useSetFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, scheduledAt, scheduledTimezone }) =>
      (await crmApi.post(`/leads/${leadId}/follow-up`, { scheduledAt, scheduledTimezone })).data,
    onSuccess: (_, vars) => invalidate(qc, vars.leadId),
  });
}

export function useClearFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId }) => (await crmApi.delete(`/leads/${leadId}/follow-up`)).data,
    onSuccess: (_, vars) => invalidate(qc, vars.leadId),
  });
}

export function useCompleteFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId }) => (await crmApi.post(`/leads/${leadId}/follow-up/complete`)).data,
    onSuccess: (_, vars) => invalidate(qc, vars.leadId),
  });
}

export function useSnoozeFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId }) => (await crmApi.post(`/leads/${leadId}/follow-up/snooze`)).data,
    onSuccess: (_, vars) => invalidate(qc, vars.leadId),
  });
}

// Reminder engine polls this every minute while a CRM Shell is mounted.
// Backend caps the window to ±2h around scheduledAt, so the response is
// always small (one row per due lead).
export function useActiveFollowUps({ enabled = true } = {}) {
  const { projectId } = useSelectedProject();
  const { companyQueryParams } = useSelectedCompany();
  return useQuery({
    queryKey: [...ACTIVE_KEY, { projectId, companyQueryParams }],
    queryFn: async () => (await crmApi.get("/follow-ups/active", {
      params: { ...(projectId ? { projectId } : {}), ...companyQueryParams },
    })).data,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 55_000,
  });
}

export function usePendingFollowUps({ scope = "mine", limit = 20, skip = 0 } = {}) {
  const { projectId } = useSelectedProject();
  const { companyQueryParams } = useSelectedCompany();
  return useQuery({
    queryKey: ["crm", "followups", "pending", { scope, limit, skip, projectId, companyQueryParams }],
    queryFn: async () => (await crmApi.get("/follow-ups/pending", {
      params: { scope, limit, skip, ...(projectId ? { projectId } : {}), ...companyQueryParams },
    })).data,
    keepPreviousData: true,
  });
}

// Org-level follow-up settings (enabled toggle + window tuning). Settings
// live on a singleton doc per org; PATCH accepts a partial payload.
const FOLLOWUP_SETTINGS_KEY = ["crm", "settings", "follow-ups"];

export function useFollowUpSettings() {
  return useQuery({
    queryKey: FOLLOWUP_SETTINGS_KEY,
    queryFn: async () => (await crmApi.get("/settings/follow-ups")).data,
    staleTime: 60_000,
  });
}

export function useUpdateFollowUpSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) => (await crmApi.patch("/settings/follow-ups", patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FOLLOWUP_SETTINGS_KEY });
      qc.invalidateQueries({ queryKey: ACTIVE_KEY });
    },
  });
}

const EMAIL_FOLLOWUP_SETTINGS_KEY = ["crm", "settings", "email-follow-ups"];
const OBLIGATIONS_KEY = ["crm", "followups", "obligations"];

export function useEmailFollowUpSettings() {
  return useQuery({
    queryKey: EMAIL_FOLLOWUP_SETTINGS_KEY,
    queryFn: async () => (await crmApi.get("/settings/email-follow-ups")).data,
    staleTime: 60_000,
  });
}

export function useUpdateEmailFollowUpSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) => (await crmApi.patch("/settings/email-follow-ups", patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: EMAIL_FOLLOWUP_SETTINGS_KEY });
      qc.invalidateQueries({ queryKey: OBLIGATIONS_KEY });
    },
  });
}

export function useEmailFollowUpObligations({
  scope = "mine", status, limit = 20, skip = 0, q, ownerId, from, to,
} = {}) {
  const { projectId } = useSelectedProject();
  const query = (q || "").trim();
  return useQuery({
    queryKey: [...OBLIGATIONS_KEY, {
      scope, status, limit, skip, projectId, q: query, ownerId, from, to,
    }],
    queryFn: async () => (await crmApi.get("/follow-ups/obligations", {
      params: {
        scope, limit, skip,
        ...(status ? { status } : {}),
        ...(projectId ? { projectId } : {}),
        ...(query ? { q: query } : {}),
        ...(ownerId ? { ownerId } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      },
    })).data,
    keepPreviousData: true,
  });
}

export function useEmailFollowUpSummary({ scope = "mine" } = {}) {
  const { projectId } = useSelectedProject();
  return useQuery({
    queryKey: [...OBLIGATIONS_KEY, "summary", { scope, projectId }],
    queryFn: async () => (await crmApi.get("/follow-ups/obligations/summary", {
      params: { scope, ...(projectId ? { projectId } : {}) },
    })).data,
    staleTime: 60_000,
  });
}

export function useCloseEmailFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ obligationId, reason }) =>
      (await crmApi.post(`/follow-ups/obligations/${obligationId}/close`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OBLIGATIONS_KEY });
      qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

export function useEscalateEmailFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ obligationId, reason, notify = true }) =>
      (await crmApi.post(`/follow-ups/obligations/${obligationId}/escalate`, { reason, notify })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OBLIGATIONS_KEY });
      qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

/** org_admin team monitor for dashboard drawer (Overdue / Due today / Pending / Complete). */
export function useEmailFollowUpAdminMonitor({ tab = "overdue", enabled = true } = {}) {
  const { projectId } = useSelectedProject();
  return useQuery({
    queryKey: [...OBLIGATIONS_KEY, "admin-monitor", { tab, projectId }],
    queryFn: async () => (await crmApi.get("/follow-ups/obligations/admin-monitor", {
      params: { tab, ...(projectId ? { projectId } : {}) },
    })).data,
    enabled,
    keepPreviousData: true,
    staleTime: 30_000,
  });
}
